import React from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { validateEnv } from '@/app/lib/@system/env'
import { initSentry } from '@/app/lib/@system/sentry'
import { info } from '@/config'
import { applyBrandColors, applyDefaultTheme } from '@/app/lib/@system/brandPrePaint'
import { activateBrandFontsAfterFirstPaint } from '@/app/lib/@system/fonts'
import { isServerRendered } from '@/app/lib/@system/initialState'
import { reportHydrationError } from '@/app/lib/@system/hydration'
import App from './App'
import './index.css'

validateEnv()
// Loads @sentry/react as its own chunk only when a DSN is configured; nothing waits on it.
initSentry()
// Brand fonts were preloaded (non-blocking) by index.html; apply them after the first paint
// (display=optional keeps the first paint's font, see lib/@system/fonts).
activateBrandFontsAfterFirstPaint()

// @system — Apply brand colors + default theme from config to CSS custom
// properties BEFORE first paint (no default flash). Logic lives in
// @system/brandPrePaint so it can be unit tested deterministically; products set
// brandColor / accentColor / defaultTheme in @custom/info.js. Both touch <html> /
// <head> only, so they are safe to run before hydrating a server-rendered #root.
applyBrandColors(info)
applyDefaultTheme(info)

// index.html always ships <div id="root">; React itself throws a clear error if it is missing.
const rootEl = /** @type {HTMLElement} */ (document.getElementById('root'))
const tree = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
)

// Public routes arrive server-rendered (`<div id="root" data-ssr="1">`, lib/@system/Ssr on the
// server + client/src/entry-server.jsx): attach to that HTML instead of replacing it, so the
// text the visitor already sees stays the Largest Contentful Paint. Everything else (the /app
// shell, routes the server did not render) mounts exactly as before.
if (isServerRendered()) {
  hydrateRoot(rootEl, tree, { onRecoverableError: reportHydrationError })
} else {
  createRoot(rootEl).render(tree)
}
